import { Injectable, Renderer2 } from '@angular/core';
import { environment } from '@env/environment';
import { PanelDef } from 'app/shared/components/panels/panels.types';
import { toCanvas } from 'html-to-image';
import jsPDF from 'jspdf';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { Image, Project } from 'app/modules/projects/projects.types';

const MAP_BOX_STATIC_URL = 'https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v11/static/';
const MAP_BOX_STATIC_ZOOM = '15.25';
const MAP_BOX_STATIC_SIZE = '600x200@2x';
@Injectable()
export class ProjectTabToPdfService {
  public renderer: Renderer2;
  private project: Project;
  private imagesUrls: Array<[]> = [];
  private panels: PanelDef<any>[];
  private imgWidth = 210;
  private pageHeight = 295;

  constructor(private projectService: ProjectsService) {}

  savePdf(project: Project, definition: { panels: []; dashboard: { panels: Array<[]> }; name: string }) {
    this.project = project;
    this.panels = this.defPanels(definition);
    const node = this.renderer.selectRootElement('#export-tab-to-pdf', true);
    const originalNodeHeight = node?.offsetHeight;

    let gridRow = document.querySelector('.gridster-column') as HTMLElement;
    if (gridRow) {
      let totalheight = gridRow.style['height'];
      node.style.height = totalheight;
    }

    const filter = (node: HTMLElement) => {
      const exclusionClasses = ['html-to-image-ignore-exporting'];
      return !exclusionClasses.some((classname) => node.classList?.contains(classname));
    };
    toCanvas(node, { filter, cacheBust: true }).then((canvas) => {
      let doc = new jsPDF('p', 'mm');

      doc = this.appendTabImage(doc, canvas);

      if (this.hasGalleryPanel()) {
        doc = this.appendGallery(doc);
      }

      if (this.hasMapPanel()) {
        doc = this.appendPropertiesLocations(doc);
      }

      doc.save(`${this.project.title} - ${definition?.name || 'Overview'}.pdf`);

      if (gridRow) {
        node.style.height = `${originalNodeHeight}px`;
      }

      this.projectService.exportPdf(false);
    });
  }

  private defPanels(definition: { panels: []; dashboard: { panels: Array<[]> }; name: string }): PanelDef<any>[] {
    if (definition.dashboard) {
      let panels = [];
      definition.dashboard.panels.forEach((element) => {
        panels.push(...element);
      });
      return panels;
    } else {
      return definition.panels;
    }
  }

  private appendTabImage(doc: jsPDF, canvas: HTMLCanvasElement): jsPDF {
    let imgData = canvas.toDataURL('image/png');
    let imgHeight = (canvas.height * this.imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    doc.addImage(imgData, 'PNG', 0, position, this.imgWidth, imgHeight);
    heightLeft -= this.pageHeight;

    while (heightLeft >= 0) {
      position += heightLeft - imgHeight; // top padding for other pages
      doc.addPage();
      doc.addImage(imgData, 'PNG', 0, position, this.imgWidth, imgHeight);
      heightLeft -= this.pageHeight;
    }

    return doc;
  }

  private appendGallery(doc: jsPDF): jsPDF {
    const imageSize = 69;
    const maxImagesAllowed = 12;

    this.imagesUrls = [];
    this.imagesUrls = this.splitImagesUrls(this.project.images);

    if (this.imagesUrls.length) {
      doc.addPage();
      doc.text('Images from gallery', 2, 10);
      this.imagesUrls.slice(0, maxImagesAllowed).forEach((images, rowIndex) => {
        images.forEach((image, colIndex) => {
          const columnPosition = imageSize * colIndex;
          const rowPosition = rowIndex === 0 ? 15 : imageSize * rowIndex;
          doc.addImage(image, 'PNG', columnPosition, rowPosition, imageSize, imageSize);
        });
      });
    }

    return doc;
  }

  private appendPropertiesLocations(doc: jsPDF): jsPDF {
    const isPortfolio = !!this.project.child_ids?.length;
    let properties = [];

    if (isPortfolio) {
      this.project.children?.forEach((child) => {
        if (child.properties?.length) {
          properties.push(child.properties[0]);
        }
      });
    } else {
      properties = this.project.properties;
      if (!properties.length && this.project.address) {
        properties = [
          {
            address: this.project.address,
          },
        ];
      }
    }

    if (properties.length) {
      doc.addPage().setFontSize(14);
      const pageTitle = isPortfolio ? 'Portfolio Locations' : 'Property Locations';
      doc.text(pageTitle, 2, 10);
      const propertiesPerPage = 3;
      const marginTopFromText = 5;
      const mapImageHeight = 70;
      const mapImageWidth = 209;
      const marginBottomFromMap = 10;

      let lastTextPosition = 25;
      properties.forEach((property, index) => {
        const locationLat = property.address.latitude;
        const locationLng = property.address.longitude;
        // eslint-disable-next-line max-len
        const mapImage = `${MAP_BOX_STATIC_URL}pin-s+ff7300(${locationLng},${locationLat})/${locationLng},${locationLat},${MAP_BOX_STATIC_ZOOM},0/${MAP_BOX_STATIC_SIZE}?access_token=${environment.mapbox.accessToken}`;

        if (isPortfolio) {
          const dealTitle = this.project.children[index].title;
          const dealAddress = property.address.address_string;
          doc.text(`  - ${dealTitle}`, 2, lastTextPosition).setFontSize(12);

          if (dealAddress.toLowerCase() !== dealTitle.toLocaleLowerCase()) {
            doc.text(`${dealAddress}`, 7, lastTextPosition + 6).setFontSize(14);
            lastTextPosition += 6;
          }
        } else {
          doc.text(` - ${property.name}`, 2, lastTextPosition);
        }

        const mapPosition = lastTextPosition + marginTopFromText;
        doc.addImage(mapImage, 'PNG', 0, mapPosition, mapImageWidth, mapImageHeight);
        lastTextPosition = mapPosition + mapImageHeight + marginBottomFromMap;

        if (index > 1 && index + 1 !== properties.length && (index + 1) % propertiesPerPage === 0) {
          doc.addPage();
          lastTextPosition = 15;
        }
      });
    }

    return doc;
  }

  private splitImagesUrls(images: Image[]): Array<[]> {
    if (!images) return [];

    const imagesPerLine = 3;
    const urls = images.map((i) => i.large_image_url);

    return urls.reduce((resultArray, item, index) => {
      const chunkIndex = Math.floor(index / imagesPerLine);
      if (!resultArray[chunkIndex]) {
        resultArray[chunkIndex] = [];
      }
      resultArray[chunkIndex].push(item);
      return resultArray;
    }, []);
  }

  private hasGalleryPanel(): boolean {
    return !!this.panels.find((p) => p.type === 'images');
  }

  private hasMapPanel(): boolean {
    return !!this.panels.find((p) => p.type === 'map');
  }
}
